
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, ChevronLeft, ChevronRight, User, Trash2 } from 'lucide-react';
import { Spinner } from '@/components/Spinner';
import { FoodItem } from './types';

interface SearchResultsProps {
  paginatedResults: FoodItem[];
  searchTerm: string;
  loading: boolean;
  currentPage: number;
  totalPages: number;
  searchResults: FoodItem[];
  resultsPerPage: number;
  onSelectFood: (food: FoodItem) => void;
  onPageChange: (page: number) => void;
  onShowCustomFoodForm: () => void;
  onDeleteCustomFood?: (foodId: string) => void;
}

const SearchResults: React.FC<SearchResultsProps> = ({
  paginatedResults,
  searchTerm,
  loading,
  currentPage,
  totalPages,
  searchResults,
  resultsPerPage,
  onSelectFood,
  onPageChange,
  onShowCustomFoodForm,
  onDeleteCustomFood
}) => {
  const totalResults = searchResults.length;
  const startResult = (currentPage - 1) * resultsPerPage + 1;
  const endResult = Math.min(currentPage * resultsPerPage, totalResults);

  const handleDeleteClick = (e: React.MouseEvent, foodId: string) => {
    e.stopPropagation();
    if (onDeleteCustomFood) {
      onDeleteCustomFood(foodId);
    }
  };

  if (!searchTerm || searchTerm.length < 2) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Spinner size="md" />
        <span className="ml-2 text-gray-600">Buscando alimentos...</span>
      </div>
    );
  }

  if (searchResults.length === 0) {
    return (
      <Card className="mt-3 border border-orange-200">
        <CardContent className="p-4 text-center">
          <div className="text-gray-500">
            <p className="mb-3">No se encontraron alimentos para "<strong>{searchTerm}</strong>"</p>
            <Button 
              variant="outline" 
              size="sm"
              className="border-dashed border-2 border-green-300 text-green-700 hover:bg-green-50"
              onClick={onShowCustomFoodForm}
            >
              <Plus size={16} className="mr-2" />
              Crear "{searchTerm}" como alimento personalizado
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {/* Results header */}
      <div className="flex justify-between items-center text-sm text-gray-600">
        <span>
          Mostrando {startResult}-{endResult} de {totalResults} resultados para "<strong>{searchTerm}</strong>"
        </span>
        {totalPages > 1 && (
          <span>Página {currentPage} de {totalPages}</span>
        )}
      </div>

      {/* Results list */}
      <div className="space-y-2">
        {paginatedResults.map((food, index) => (
          <Card 
            key={`${food.id}-${index}`} 
            className="border border-gray-200 hover:border-green-300 transition-colors cursor-pointer"
            onClick={() => onSelectFood(food)}
          >
            <CardContent className="p-3">
              <div className="flex justify-between items-center">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-gray-800">{food.name}</h4>
                    {food.source === 'Personalizado' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-full">
                        <User size={12} />
                        Personalizado
                      </span>
                    )}
                    {food.source && food.source !== 'Personalizado' && (
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {food.source}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 flex gap-4 mt-1">
                    <span>{food.calories} kcal</span>
                    <span>P: {food.protein}g</span>
                    <span>C: {food.carbs}g</span>
                    <span>G: {food.fat}g</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  {food.source === 'Personalizado' && onDeleteCustomFood && (
                    <Button 
                      size="sm" 
                      variant="ghost"
                      className="text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                      onClick={(e) => handleDeleteClick(e, food.id)}
                    >
                      <Trash2 size={16} />
                    </Button>
                  )}
                  <Button 
                    size="sm" 
                    variant="ghost"
                    className="text-green-600 hover:bg-green-50"
                  >
                    <Plus size={16} />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 pt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="flex items-center gap-1"
          >
            <ChevronLeft size={16} />
            Anterior
          </Button>
          
          <div className="flex gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <Button
                  key={pageNum}
                  variant={pageNum === currentPage ? "default" : "outline"}
                  size="sm"
                  onClick={() => onPageChange(pageNum)}
                  className={pageNum === currentPage ? "bg-green-600 hover:bg-green-700" : ""}
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="flex items-center gap-1"
          >
            Siguiente
            <ChevronRight size={16} />
          </Button>
        </div>
      )}
    </div>
  );
};

export default SearchResults;
